/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1674086400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Thu Jan 19 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "mparticle",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Getting started",
          "depth": 0,
          "id": "getting-started"
        }, {
          "value": "Retrieve API key and API secret",
          "depth": 1,
          "id": "retrieve-api-key-and-api-secret"
        }, {
          "value": "Syncing data",
          "depth": 0,
          "id": "syncing-data"
        }, {
          "value": "Users",
          "depth": 1,
          "id": "users"
        }, {
          "value": "Events",
          "depth": 1,
          "id": "events"
        }, {
          "value": "Custom event",
          "depth": 1,
          "id": "custom-event"
        }, {
          "value": "Commerce event",
          "depth": 1,
          "id": "commerce-event"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      ul: "ul",
      li: "li",
      a: "a",
      h3: "h3",
      ol: "ol",
      img: "img",
      h4: "h4",
      code: "code"
    }, _provideComponents(), props.components), {EventSyncWarning, NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!EventSyncWarning) _missingMdxReference("EventSyncWarning", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Through mParticle's diverse integration library, Hightouch users can sync enriched customer profiles and events to hundreds of partner integrations. Use your data warehouse as the source of truth and keep your team's tools up to date with the latest data using Hightouch and mParticle."
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Users"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source as user attributes"
            }), _jsx(_components.td, {
              children: "Upsert"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Events"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source as commerce events and custom events"
            }), _jsx(_components.td, {
              children: "Insert"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "getting-started",
        children: "Getting started"
      }), "\n", _jsx(_components.p, {
        children: "To give Hightouch access to mParticle, you need to enter:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "API key"
        }), "\n", _jsx(_components.li, {
          children: "API secret"
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Follow the instructions in the ", _jsx(_components.a, {
          href: "#retrieve-api-key-and-api-secret",
          children: "following sections"
        }), " to retrieve your API key and API secret."]
      }), "\n", _jsx(_components.h3, {
        id: "retrieve-api-key-and-api-secret",
        children: "Retrieve API key and API secret"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Navigate to ", _jsx(_components.a, {
            href: "https://app.mparticle.com/setup/inputs/feeds",
            children: "Inputs"
          }), " in your mParticle account."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-mparticle-setup-1.png",
          alt: "Click on the \"+\" button on the Hightouch tab"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "2",
        children: ["\n", _jsxs(_components.li, {
          children: ["Add a feed under the ", _jsx(_components.strong, {
            children: "Hightouch"
          }), " tab by clicking the ", _jsx(_components.strong, {
            children: "+"
          }), " button."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-mparticle-setup-2.png",
          alt: "Configure your feed"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "3",
        children: ["\n", _jsxs(_components.li, {
          children: ["Give your configuration a name and select ", _jsx(_components.strong, {
            children: "Web"
          }), " under ", _jsx(_components.strong, {
            children: "Act as Platform"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-mparticle-setup-3.png",
          alt: "Copy your generated keys"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "4",
        children: ["\n", _jsxs(_components.li, {
          children: ["Copy your generated keys. Your ", _jsx(_components.strong, {
            children: "Server to Server Key"
          }), " is your ", _jsx(_components.strong, {
            children: "API key"
          }), " and your ", _jsx(_components.strong, {
            children: "Server to Server Secret"
          }), " is your ", _jsx(_components.strong, {
            children: "API secret"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "syncing-data",
        children: "Syncing data"
      }), "\n", _jsx(_components.h3, {
        id: "users",
        children: "Users"
      }), "\n", _jsx(_components.p, {
        children: "Sync data from any source as user attributes without an event to mParticle."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["You can match rows in your model with users in mParticle with the provided identifiers. Hightouch uses the identifier you select in this section and as the ", _jsx(_components.code, {
          children: "external Id"
        }), " for this sync."]
      }), "\n", _jsx(_components.h4, {
        id: "additional-identifiers",
        children: "Additional identifiers"
      }), "\n", _jsxs(_components.p, {
        children: ["In addition to the column selected for ", _jsx(_components.a, {
          href: "#record-matching",
          children: "record matching"
        }), ", you can choose to provide more user identifiers in this section. There is no limit to the number of columns you can select here."]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your source to mParticle as user attributes. If the customer attribute doesn't exist, Hightouch creates it."
      }), "\n", _jsx(_components.h4, {
        id: "custom-field-mapping",
        children: "Custom field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your source to mParticle as custom user attributes. If the customer attribute doesn't exist, Hightouch creates it."
      }), "\n", _jsx(_components.h4, {
        id: "environment",
        children: "Environment"
      }), "\n", _jsx(_components.p, {
        children: "In this section, select the environment you would like to sync your data to. The available choices are:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Environment"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Production"
              })
            }), _jsx(_components.td, {
              children: "All data synced is treated as production data and is forwarded to integrations for your application"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Development"
              })
            }), _jsx(_components.td, {
              children: "All data synced is treated as development data and is siloed from your production data"
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["mParticle recommends using ", _jsx(_components.strong, {
          children: "Development"
        }), " mode first to ensure the data meets your expectations before moving to ", _jsx(_components.strong, {
          children: "Production"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "delete-behavior",
        children: "Delete behavior"
      }), "\n", _jsx(_components.p, {
        children: "You have the following options for how Hightouch should handle rows leaving your query results:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Behavior"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Do nothing"
              })
            }), _jsx(_components.td, {
              children: "Keep the customer in mParticle"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Clear Fields"
              })
            }), _jsx(_components.td, {
              children: "Clear the mapped fields but keep the customer record"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "events",
        children: "Events"
      }), "\n", _jsx(_components.p, {
        children: "You can sync the following event types:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Custom Event"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Commerce Event"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "events-and-historical-events",
        children: "Events and historical events"
      }), "\n", _jsxs(_components.p, {
        children: ["Given a valid ", _jsx(_components.code, {
          children: "Date"
        }), ", ", _jsx(_components.code, {
          children: "DateTime"
        }), ", or ", _jsx(_components.code, {
          children: "timestamp"
        }), " column as a ", _jsx(_components.code, {
          children: "timestamp"
        }), " for events, Hightouch separates events based on the age of the data."]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Hightouch sends events that are older than ", _jsx(_components.strong, {
            children: "28"
          }), " days to the ", _jsx(_components.code, {
            children: "/v2/bulkevents/historical"
          }), " endpoint."]
        }), "\n", _jsxs(_components.li, {
          children: ["Hightouch sends events that are newer than ", _jsx(_components.strong, {
            children: "28"
          }), " days to the ", _jsx(_components.code, {
            children: "/v2/bulkevents"
          }), " endpoint."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "If you choose not to provide a timestamp for your events, Hightouch uses the time the event arrives at the server. That means all events are treated as newer than 28 days."
      }), "\n", _jsx(EventSyncWarning, {}), "\n", _jsx(_components.h3, {
        id: "custom-event",
        children: "Custom event"
      }), "\n", _jsx(_components.p, {
        children: "Sync data from any source as a custom event."
      }), "\n", _jsx(_components.h4, {
        id: "event-name",
        children: "Event name"
      }), "\n", _jsx(_components.p, {
        children: "Provide an event name. This is how you can reference the event in mParticle campaigns or segments."
      }), "\n", _jsx(_components.h4, {
        id: "event-timestamp",
        children: "Event timestamp"
      }), "\n", _jsx(_components.p, {
        children: "Provide a timestamp of when the event occurred. If this field is empty, Hightouch uses the time the event arrives at the server."
      }), "\n", _jsxs(_components.p, {
        children: ["Any ", _jsx(_components.code, {
          children: "DateTime"
        }), " value provided from your source is converted to Unix timestamp as expected by mParticle."]
      }), "\n", _jsx(_components.h4, {
        id: "custom-event-type",
        children: "Custom event type"
      }), "\n", _jsx(_components.p, {
        children: "Select the type of custom event you would like to sync out of the provided selections."
      }), "\n", _jsx(_components.h4, {
        id: "user-identifiers",
        children: "User identifiers"
      }), "\n", _jsx(_components.p, {
        children: "In this section, you can provide user identifiers for each event that occurred."
      }), "\n", _jsx(_components.h4, {
        id: "event-attributes-field-mapping",
        children: "Event attributes field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your source to mParticle as event attributes. If the event attribute doesn't exist, Hightouch creates it."
      }), "\n", _jsx(_components.h4, {
        id: "event-attributes-custom-field-mapping",
        children: "Event attributes custom field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your source to mParticle as custom event attributes. If the custom event attribute doesn't exist, Hightouch creates it."
      }), "\n", _jsx(_components.h4, {
        id: "user-attributes-field-mapping",
        children: "User attributes field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your source to mParticle as user attributes. If the customer attribute doesn't exist, Hightouch creates it."
      }), "\n", _jsx(_components.h4, {
        id: "user-attributes-custom-field-mapping",
        children: "User attributes custom field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your source to mParticle as custom user attributes. If the customer attribute doesn't exist, Hightouch creates it."
      }), "\n", _jsx(_components.h4, {
        id: "environment-1",
        children: "Environment"
      }), "\n", _jsx(_components.p, {
        children: "In this section, select the environment you would like to sync your data to. The available choices are:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Environment"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Production"
              })
            }), _jsx(_components.td, {
              children: "All data synced is treated as production data and is forwarded to integrations for your application"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Development"
              })
            }), _jsx(_components.td, {
              children: "All data synced is treated as development data and is siloed from your production data"
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["mParticle recommends using ", _jsx(_components.strong, {
          children: "Development"
        }), " mode first to ensure the data meets your expectations before moving to ", _jsx(_components.strong, {
          children: "Production"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "commerce-event",
        children: "Commerce event"
      }), "\n", _jsx(_components.p, {
        children: "Sync data from any source as a commerce event."
      }), "\n", _jsx(_components.h4, {
        id: "event-timestamp-1",
        children: "Event timestamp"
      }), "\n", _jsx(_components.p, {
        children: "Provide a timestamp of when the event occurred. If this field is empty, Hightouch uses the time the event arrives at the server."
      }), "\n", _jsxs(_components.p, {
        children: ["Any ", _jsx(_components.code, {
          children: "DateTime"
        }), " value provided from your source is converted to Unix timestamp as expected by mParticle."]
      }), "\n", _jsx(_components.h4, {
        id: "user-identifiers-1",
        children: "User identifiers"
      }), "\n", _jsx(_components.p, {
        children: "In this section, you can provide user identifiers for each event that occurred."
      }), "\n", _jsx(_components.h4, {
        id: "event-attributes-field-mapping-1",
        children: "Event attributes field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your source to mParticle as event attributes. If the event attribute doesn't exist, Hightouch creates it."
      }), "\n", _jsx(_components.h4, {
        id: "event-attributes-custom-field-mapping-1",
        children: "Event attributes custom field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your source to mParticle as custom event attributes. If the custom event attribute doesn't exist, Hightouch creates it."
      }), "\n", _jsx(_components.h4, {
        id: "user-attributes-field-mapping-1",
        children: "User attributes field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your source to mParticle as user attributes. If the customer attribute doesn't exist, Hightouch creates it."
      }), "\n", _jsx(_components.h4, {
        id: "user-attributes-custom-field-mapping-1",
        children: "User attributes custom field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your source to mParticle as custom user attributes. If the customer attribute doesn't exist, Hightouch creates it."
      }), "\n", _jsx(_components.h4, {
        id: "environment-2",
        children: "Environment"
      }), "\n", _jsx(_components.p, {
        children: "In this section, select the environment you would like to sync your data to. The available choices are:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Environment"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Production"
              })
            }), _jsx(_components.td, {
              children: "All data synced is treated as production data and is forwarded to integrations for your application"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Development"
              })
            }), _jsx(_components.td, {
              children: "All data synced is treated as development data and is siloed from your production data"
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["mParticle recommends using ", _jsx(_components.strong, {
          children: "Development"
        }), " mode first to ensure the data meets your expectations before moving to ", _jsx(_components.strong, {
          children: "Production"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
